/* eslint-disable indent */
import MailboxTableHeader from '@src/main/modules/messages/ui/mailbox/components/mailbox/table/MailboxTableHeader';
import { useCommercialEvaluations } from '@src/main/modules/reports/commercial/application/hook/useCommercialEvaluations';
import { useDetailedCommercialEvaluations } from '@src/main/modules/reports/commercial/application/hook/useDetailedCommercialEvaluations';
import { emptyEvaluationCriteria } from '@src/main/modules/reports/shared/domain/evaluation.interface';
import {
  getAircraftRegistrationColumnProps,
  getArrivalAirportColumnProps,
  getCarrierCodeColumnProps,
  getDepartureAirportColumnProps,
  getEvaluatedEmployeeIdColumnProps,
  getEvaluatedEmployeeNameColumnProps,
  getFlightDateColumnProps,
  getFlightNumberColumnProps,
  getReceptionDateColumnProps,
  getSupervisorEmployeeIdColumnProps,
} from '@src/main/modules/reports/ui/components/table/EvaluationColumns';
import ReportsTable from '@src/main/modules/reports/ui/components/table/ReportsTable';
import { useCommercialEvaluationActions } from '@src/main/modules/reports/ui/hooks/useCommercialEvaluationActions';
import { useCommercialEvaluationColumnBodies } from '@src/main/modules/reports/ui/hooks/useCommercialEvaluationColumnBodies';
import { useCommercialEvaluationExport } from '@src/main/modules/reports/ui/hooks/useCommercialEvaluationExport';
import { useEvaluationDateFilter } from '@src/main/modules/reports/ui/hooks/useEvaluationDateFilter';
import { useEvaluationFilterHandler } from '@src/main/modules/reports/ui/hooks/useEvaluationFilterHandler';
import { useEvaluationFilters } from '@src/main/modules/reports/ui/hooks/useEvaluationFilters';
import { mapEvaluationFiltersToCriteria } from '@src/main/modules/reports/ui/mapEvaluationFiltersToCriteria';
import InvalidDateRangeDialog from '@src/main/shared/ui/components/calendar/InvalidDateRangeDialog';
import ConfirmDeleteDialog from '@src/main/shared/ui/components/dialog/ConfirmDeleteDialog';
import AppLoading from '@src/main/shared/ui/components/spinner/AppLoading';
import { getThreeMonthDateRange } from '@src/main/shared/utils/dateUtils';
import { Column } from 'primereact/column';
import { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './PunctualityView.module.scss';

const CommercialEvaluationsView: FC = () => {
  const { t } = useTranslation();
  const { startDate, endDate } = useMemo(() => getThreeMonthDateRange(), []);
  const initialFilters = useEvaluationFilters(startDate, endDate, 'commercial');

  const [invalidDateModalVisible, setInvalidDateModalVisible] = useState(false);

  const evaluationsHook = useCommercialEvaluations(initialFilters, mapEvaluationFiltersToCriteria, {
    ...emptyEvaluationCriteria,
    sortField: 'flightDate',
    sortOrder: 'desc',
  });

  const {
    filters,
    setFilters,
    evaluations,
    total,
    isLoading,
    isFetching,
    pageNumber,
    pageSize,
    handlePageChange,
    refetchEvaluations,
    handleSort,
    sortField,
    sortOrder,
  } = evaluationsHook;

  const {
    downloadPdf,
    handleDeleteEvaluation,
    confirmDeleteEvaluation,
    rejectDeleteEvaluation,
    evaluationToDelete,
    isDeleteModalVisible,
  } = useCommercialEvaluationActions();

  const { pdfColumnBody, deleteColumnBody } = useCommercialEvaluationColumnBodies(
    t,
    downloadPdf,
    handleDeleteEvaluation,
  );

  const { handleSetFilters } = useEvaluationFilterHandler(
    setFilters,
    refetchEvaluations,
    setInvalidDateModalVisible,
  );

  const flightDateFilterTemplate = useEvaluationDateFilter({
    t,
    setInvalidDateModalVisible,
  });

  const receptionDateFilterTemplate = useEvaluationDateFilter({
    t,
    setInvalidDateModalVisible,
  });

  const detailedCriteria = useMemo(
    () =>
      mapEvaluationFiltersToCriteria(filters, {
        ...emptyEvaluationCriteria,
        offset: 0,
        limit: Math.max(total, 1),
        sortField,
        sortOrder,
      }),
    [filters, total, sortField, sortOrder],
  );

  const { data: detailedEvaluations = [] } = useDetailedCommercialEvaluations(detailedCriteria);
  const { onExportExcel } = useCommercialEvaluationExport(
    detailedEvaluations,
    'informe_evaluaciones_comerciales',
  );

  const header = (
    <MailboxTableHeader
      onRefresh={refetchEvaluations}
      onClearFilters={() => {
        setFilters(initialFilters);
        refetchEvaluations();
      }}
      onExportExcel={onExportExcel}
      showExportButtons
    />
  );

  return (
    <div className={styles.container}>
      <AppLoading isLoading={isLoading || isFetching}>
        <ReportsTable
          data={evaluations}
          filters={filters}
          setFilters={handleSetFilters}
          header={header}
          loading={isLoading}
          dataKey="id"
          globalFilterFields={[
            'carrierCode',
            'flightNumber',
            'departureAirport',
            'arrivalAirport',
            'aircraftRegistration',
            'supervisorEmployeeId',
            'evaluatedEmployeeId',
          ]}
          totalRecords={total}
          first={pageNumber * pageSize}
          rows={pageSize}
          onPage={handlePageChange}
          onSort={handleSort}
          sortField={sortField}
          sortOrder={sortOrder}
        >
          <Column {...getFlightDateColumnProps(t, flightDateFilterTemplate)} />
          <Column {...getCarrierCodeColumnProps(t)} />
          <Column {...getFlightNumberColumnProps(t)} />
          <Column {...getDepartureAirportColumnProps(t)} />
          <Column {...getArrivalAirportColumnProps(t)} />
          <Column {...getAircraftRegistrationColumnProps(t)} />
          <Column {...getSupervisorEmployeeIdColumnProps(t)} />
          <Column {...getEvaluatedEmployeeIdColumnProps(t)} />
          <Column {...getEvaluatedEmployeeNameColumnProps(t)} />
          <Column {...getReceptionDateColumnProps(t, receptionDateFilterTemplate)} />
          <Column body={pdfColumnBody} header={t('common.pdf')} />
          <Column body={deleteColumnBody} header={t('common.delete')} />
        </ReportsTable>

        <InvalidDateRangeDialog
          invalidDateRangeModalVisible={invalidDateModalVisible}
          setInvalidDateRangeModalVisible={setInvalidDateModalVisible}
          t={t}
        />

        <ConfirmDeleteDialog
          confirmDelete={() => confirmDeleteEvaluation(refetchEvaluations)}
          rejectDelete={rejectDeleteEvaluation}
          visible={isDeleteModalVisible}
          message={t('common.message.confirmDelete', {
            flight: evaluationToDelete?.flightNumber,
            carrier: evaluationToDelete?.carrierCode,
          })}
        />
      </AppLoading>
    </div>
  );
};

export default CommercialEvaluationsView;
